import styled from '@emotion/styled';
import { alpha, Stack, SxProps, Theme, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import TableCollapse from '@/components/_common/Table/TableCollapse';
import { Spinner } from '@/components/_common';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { PolarisIcon, UserDisplay } from '@/exports/columnCommonComponents';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectAudioLanguagesMap, selectSubtitlesLanguagesMap } from '@/redux/selectors/constantsSelectors';
import {
  selectIncrementalSchedulingsByVersionSchedulingId,
  selectIsIncrementalSchedulingLoadingByVersionSchedulingId
} from '@/redux/selectors/focusScheduleSelectors';
import { fetchIncrementalSchedulingsByVersionSchedulingId } from '@/redux/slices/focusScheduleSlice';
import { FocusScheduling, ScheduleAvailType } from '@/redux/slices/slice_types';
import SchedulingTermDateDisplay from '../SchedulingTermDateDisplay';
import AvailScheduleStatus from '../AvailScheduleStatus';
import AvailScheduleRowExpandFilters, { LaunchStatusFilter } from './AvailScheduleRowExpandFilters';
import LaunchTypeChip from '../NewSchedule/ReviewSchedule/AddIncrementalLaunch/LaunchTypeChip';
import { formatDateTimePartsLocal } from '../../utils/newScheduleAvails';
import AvailScheduleRowExpandOverflowActions from './AvailScheduleRowExpandOverflowActions';
import AvailScheduleLaunchChip from './AvailScheduleLaunchChip';

type AvailScheduleRowExpandProps = {
  item: FocusScheduling;
  isCollapsed: boolean;
};

const LAUNCH_TABLE_CLASS_NAME = 'launch-table';

const launchChipButtonSx: SxProps<Theme> = {
  backgroundColor: (theme) => (theme as FocusTheme).palette.neutral[80],
  borderColor: (theme) => (theme as FocusTheme).palette.neutral[70]
};

const getScheduleAvailValues = (schedule: FocusScheduling, type: ScheduleAvailType) => {
  if (type === 'territories') {
    return schedule.territory ?? [];
  }

  if (type === 'audio') {
    return schedule.audio_languages ?? [];
  }

  return schedule.timed_text_languages ?? [];
};

const getPreviousLaunch = (launch: FocusScheduling, schedulings: FocusScheduling[], index: number) => {
  if (launch.prev_scheduling_id) {
    return schedulings.find((scheduling) => scheduling.id === launch.prev_scheduling_id);
  }

  return schedulings[index - 1];
};

const AvailScheduleRowExpand = ({ item, isCollapsed }: AvailScheduleRowExpandProps) => {
  const dispatch = useAppDispatch();
  const audioLanguagesMap = useAppSelector(selectAudioLanguagesMap);
  const subtitleLanguagesMap = useAppSelector(selectSubtitlesLanguagesMap);
  const schedulings = useAppSelector((state) => selectIncrementalSchedulingsByVersionSchedulingId(state, item.id));
  const isFetching = useAppSelector((state) => selectIsIncrementalSchedulingLoadingByVersionSchedulingId(state, item.id));

  const [selectedStatus, setSelectedStatus] = useState<LaunchStatusFilter>('all');

  const filteredLaunches = schedulings.filter((scheduling) => {
    if (selectedStatus === 'all') return true;

    return scheduling.status === selectedStatus;
  });

  // NATALIA: Need an enum that represents the status and the correct string
  const selectedStatusLabel = selectedStatus === 'confirmed' ? 'active' : selectedStatus;

  useEffect(() => {
    if (!isCollapsed) {
      dispatch(fetchIncrementalSchedulingsByVersionSchedulingId({ versionSchedulingId: item.id }));
    }
  }, [dispatch, isCollapsed, item.id]);

  return (
    <ExpandableRow sx={{ margin: '-16px', width: 'calc(100% + 32px)' }} isCollapsed={isCollapsed}>
      {isFetching ? (
        <Spinner />
      ) : (
        <>
          <AvailScheduleRowExpandFilters launchCount={filteredLaunches.length} selectedStatus={selectedStatus} onStatusChange={setSelectedStatus} />
          <LaunchTable className={LAUNCH_TABLE_CLASS_NAME}>
            <LaunchTableHead>
              <LaunchHeaderRow>
                <LaunchHeaderCell>Window Term</LaunchHeaderCell>
                <LaunchHeaderCell>Audio</LaunchHeaderCell>
                <LaunchHeaderCell>Timed Text</LaunchHeaderCell>
                <LaunchHeaderCell>Territories</LaunchHeaderCell>
                <LaunchHeaderCell>Type</LaunchHeaderCell>
                <LaunchHeaderCell>Last Modified</LaunchHeaderCell>
                <StatusLaunchHeaderCell>Status</StatusLaunchHeaderCell>
                <ActionsLaunchHeaderCell>Actions</ActionsLaunchHeaderCell>
              </LaunchHeaderRow>
            </LaunchTableHead>
            <LaunchTableBody>
              {filteredLaunches.length === 0 ? (
                <LaunchRow>
                  <EmptyLaunchCell colSpan={8}>No launches with {selectedStatusLabel} status</EmptyLaunchCell>
                </LaunchRow>
              ) : (
                filteredLaunches.map((launch) => {
                  const updatedBy = launch?.updated_by ?? '';
                  const { date: updatedDate, time: updatedTime } = formatDateTimePartsLocal(launch.updated_at, 'en-US', 'short');
                  const launchIndex = schedulings.findIndex((scheduling) => scheduling.id === launch.id);
                  const previousLaunch = getPreviousLaunch(launch, schedulings, launchIndex);
                  const isFirstLaunch = !launch.prev_scheduling_id && launchIndex === 0;

                  const audioValues = getScheduleAvailValues(launch, 'audio');
                  const timedTextValues = getScheduleAvailValues(launch, 'timedtext');
                  const territoryValues = getScheduleAvailValues(launch, 'territories');

                  const previousAudioValues = previousLaunch ? getScheduleAvailValues(previousLaunch, 'audio') : [];
                  const previousTimedTextValues = previousLaunch ? getScheduleAvailValues(previousLaunch, 'timedtext') : [];
                  const previousTerritoryValues = previousLaunch ? getScheduleAvailValues(previousLaunch, 'territories') : [];

                  return (
                    <LaunchRow key={launch.id}>
                      {!isCollapsed && (
                        <>
                          <LaunchCell>
                            <Stack direction="row" gap={4}>
                              <SchedulingTermDateDisplay label="Term Start" value={launch.window_start} />
                              <SchedulingTermDateDisplay label="Term End" value={launch.window_end} />
                            </Stack>
                          </LaunchCell>
                          <LaunchCell>
                            <AvailScheduleLaunchChip
                              icon={<PolarisIcon icon="music" />}
                              items={audioValues}
                              previousItems={previousAudioValues}
                              launchType={isFirstLaunch ? 'first-time' : 'incremental'}
                              type="audio"
                              allLanguagesMap={audioLanguagesMap}
                              sx={launchChipButtonSx}
                            />
                          </LaunchCell>
                          <LaunchCell>
                            <AvailScheduleLaunchChip
                              icon={<PolarisIcon icon="subtitles" />}
                              items={timedTextValues}
                              previousItems={previousTimedTextValues}
                              launchType={isFirstLaunch ? 'first-time' : 'incremental'}
                              type="timedtext"
                              allLanguagesMap={subtitleLanguagesMap}
                              sx={launchChipButtonSx}
                            />
                          </LaunchCell>
                          <LaunchCell>
                            <AvailScheduleLaunchChip
                              icon={<PolarisIcon icon="map" />}
                              items={territoryValues}
                              previousItems={previousTerritoryValues}
                              launchType={isFirstLaunch ? 'first-time' : 'incremental'}
                              type="territories"
                              sx={launchChipButtonSx}
                            />
                          </LaunchCell>
                          <LaunchCell>
                            <LaunchTypeChip type={isFirstLaunch ? 'first-time' : 'incremental'} appearance="rights-change" />
                          </LaunchCell>
                          <LaunchCell>
                            <Stack spacing={0}>
                              <Typography variant="body1">{updatedBy ? <UserDisplay user={updatedBy} /> : 'System'}</Typography>
                              <StyledUpdatedAt variant="caption">
                                {updatedDate} {updatedTime}
                              </StyledUpdatedAt>
                            </Stack>
                          </LaunchCell>
                          <StatusLaunchCell>
                            <AvailScheduleStatus row={launch} />
                          </StatusLaunchCell>
                          <ActionsLaunchCell>
                            <AvailScheduleRowExpandOverflowActions row={launch} versionScheduling={item} />
                          </ActionsLaunchCell>
                        </>
                      )}
                    </LaunchRow>
                  );
                })
              )}
            </LaunchTableBody>
          </LaunchTable>
        </>
      )}
    </ExpandableRow>
  );
};

export default AvailScheduleRowExpand;

const ExpandableRow = styled(TableCollapse)`
  background: ${({ theme }) => theme.palette.background.paper};

  > td.MuiTableCell-root {
    padding: 0;
    border-right: 0;
  }

  & .MuiCollapse-wrapper,
  & .MuiCollapse-wrapperInner {
    background: ${({ theme }) => theme.palette.background.default};
  }
`;

const LaunchTable = styled.table`
  &.${LAUNCH_TABLE_CLASS_NAME} {
    width: 100%;
    border-spacing: 0;
    border-collapse: separate;
  }
`;

const LaunchTableHead = styled.thead`
  .${LAUNCH_TABLE_CLASS_NAME} && {
    background-color: ${alpha('#1d2c47', 0.37)};
  }
`;

const LaunchHeaderRow = styled.tr``;

const LaunchHeaderCell = styled.th`
  .${LAUNCH_TABLE_CLASS_NAME} thead && {
    border: 0;
    padding: ${({ theme }) => theme.spacing(1.5, 2)};
    text-align: left;
    text-wrap: nowrap;
    vertical-align: middle;
  }

  .${LAUNCH_TABLE_CLASS_NAME} thead &&:first-of-type {
    padding-left: ${({ theme }) => theme.spacing(3)};
  }
`;

const StatusLaunchHeaderCell = styled(LaunchHeaderCell)`
  .${LAUNCH_TABLE_CLASS_NAME} thead && {
    width: 120px;
  }
`;

const ActionsLaunchHeaderCell = styled(LaunchHeaderCell)`
  .${LAUNCH_TABLE_CLASS_NAME} thead && {
    width: 72px;
    text-align: right;
  }
`;

const LaunchTableBody = styled.tbody``;

const LaunchRow = styled.tr`
  .${LAUNCH_TABLE_CLASS_NAME} tbody && {
    color: ${({ theme }) => theme.palette.text.primary};
    transition: ease background-color 0.2s;
  }

  .${LAUNCH_TABLE_CLASS_NAME} tbody &&:hover {
    cursor: pointer;
  }
`;

const LaunchCell = styled.td`
  .${LAUNCH_TABLE_CLASS_NAME} tbody && {
    border: 0;
    border-right: 1px solid ${({ theme }) => alpha((theme as FocusTheme).palette.neutral[70], 0.7)};
    padding: ${({ theme }) => theme.spacing(2)};
    overflow-wrap: anywhere;
    vertical-align: middle;
  }

  .${LAUNCH_TABLE_CLASS_NAME} tbody &&:first-of-type {
    padding-left: ${({ theme }) => theme.spacing(3)};
  }

  .${LAUNCH_TABLE_CLASS_NAME} tbody &&:last-of-type {
    border-right: 0;
  }
`;

const StatusLaunchCell = styled(LaunchCell)`
  .${LAUNCH_TABLE_CLASS_NAME} tbody && {
    width: 120px;
  }
`;

const ActionsLaunchCell = styled(LaunchCell)`
  .${LAUNCH_TABLE_CLASS_NAME} tbody && {
    width: 72px;
    text-align: right;
  }
`;

const EmptyLaunchCell = styled.td`
  .${LAUNCH_TABLE_CLASS_NAME} tbody && {
    border: 0;
    padding: ${({ theme }) => theme.spacing(4, 3)};
    text-align: center;
    color: ${({ theme }) => theme.palette.text.secondary};
    font-weight: 500;
  }
`;

const StyledUpdatedAt = styled(Typography)`
  font-weight: 500;
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[10]};
`;
